<template>
  <v-dialog v-model="dialog" max-width="960px">

    <template #activator="{ on }">
      <slot name="button" :activator="on">
        <permission-button
            v-on="on"
            :permission="`schema_create`"
            :color="`primary`"
            :button-class="`mr-0 ml-0`"
            :tooltip="{right: true, bottom: false, attach: false}"
        >
          {{ 'app.add_schema' | trans }}
        </permission-button>
      </slot>
    </template>

    <v-stepper v-model="step" v-if="model">
      <v-stepper-header class="v-toolbar v-toolbar--card theme--dark primary">
        <v-stepper-step :complete="step > 1" step="1" :rules="[() => valid]">{{ formTitle }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" step="2" :rules="[validateStepTwo]">
          {{ 'app.schema_step_2' | trans }}
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="3" v-if="model.schema !== undefined" :rules="[() => step3Valid]">
          {{ 'app.schema_step_3' | trans }}
        </v-stepper-step>
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content step="1">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12>
                  <v-text-field :error-messages="titleError" v-model="model.title"
                                :rules="rules.title" required
                                :label="trans('app.name')"
                                @input="debounceValidateName"
                                solo
                  >

                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field v-model.number="model.desired_diet_energy"
                                :rules="rules.desired_diet_energy"
                                required
                                suffix="kcal"
                                type="number"
                                step="100"
                                solo
                                :label="trans('app.desired_kcal')"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                      :label="trans('app.number_of_days_label', {
                      maxMenuDaysLength: maxMenuDaysLength
                    })"
                      v-model="model.days"
                      full
                      :suffix="`(1 - ${maxMenuDaysLength})`"
                      :rules="rules.days"
                      type="number"
                      solo
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 d-flex>
                  <v-combobox
                      multiple
                      clearable
                      v-model="dietsTags"
                      :search-input.sync="syncDiet"
                      :items="dietsItems"
                      item-text="name"
                      :label="trans('page.tags.diet')"
                      solo
                  >
                    <template v-slot:no-data>
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                                                        <span v-if="dietsItems.length > 0"
                                                              v-html="trans('app.no_tag', {type: 'dieta', tag: syncDiet})"></span>
                            <span v-else
                                  v-html="trans('app.tag_collection_empty', {type: 'dieta', tag: syncDiet})"></span>
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </template>

                  </v-combobox>
                </v-flex>
                <v-flex xs12 d-flex>
                  <v-combobox
                      multiple
                      clearable
                      v-model="schemasTags"
                      :search-input.sync="syncSchemas"
                      :items="schemasItems"
                      item-text="name"
                      :label="trans('app.tags.tags')"
                      solo
                  >
                    <template v-slot:no-data>
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                                                        <span v-if="schemasItems.length > 0"
                                                              v-html="trans('app.no_tag', {type: 'schema', tag: syncSchemas})"></span>
                            <span v-else
                                  v-html="trans('app.tag_collection_empty', {type: 'schema', tag: syncSchemas})"></span>
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </template>

                  </v-combobox>
                </v-flex>

                <v-flex xs6 d-flex v-if="hasPermission('schema_system_create')">
                  <v-checkbox
                      v-model="model.system"
                      :label="trans('app.system')"
                      color="primary"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs6 d-flex>
                  <v-checkbox
                      v-model="model.favorited"
                      required
                      :label="trans('app.favorited')"
                      color="primary"
                  ></v-checkbox>
                </v-flex>

              </v-layout>
            </v-container>
          </v-form>
          <v-divider></v-divider>
          <v-layout align-center justify-end row fill-height>
            <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
            <v-btn color="primary" @click="validate(2)" :disabled="!valid">{{ 'app.next' | trans }}</v-btn>
          </v-layout>
        </v-stepper-content>
        <v-stepper-content step="2">
          <v-card flat>
            <v-card-text>
              <v-form ref="step2form" v-model="step2Valid" @input="formUpdated"
                      v-if="step === 2">
                <v-layout row wrap justify-center>
                  <v-flex xs10 slider-nutrient>
                    <v-slider
                        thumb-label="always"
                        :label="trans('app.nutrients.carbo')"
                        v-model="percentage.carbo"
                        :max="100"
                        :min="1"
                        :step="1"
                        :thumb-size="24"
                        :rules="[schynchronized('carbo')]"
                    ></v-slider>
                  </v-flex>

                  <v-flex xs2 class="text-xs-center">
                    {{ calculateGramsFromPercentage(percentage.carbo, 'Węglowodany') | integers }}g
                  </v-flex>

                  <v-flex xs10 slider-nutrient>
                    <v-slider
                        thumb-label="always"
                        :label="trans('app.nutrients.protein')"
                        v-model="percentage.protein"
                        :rules="[schynchronized('protein')]"
                        :max="100"
                        :min="1"
                        :step="1"
                        :thumb-size="24"
                    ></v-slider>
                  </v-flex>

                  <v-flex xs2 class="text-xs-center">
                    {{ calculateGramsFromPercentage(percentage.protein, 'Białko') | integers }}g
                  </v-flex>

                  <v-flex xs10 slider-nutrient>
                    <v-slider
                        thumb-label="always"
                        :label="trans('app.nutrients.fats')"
                        v-model="percentage.fat"
                        :rules="[schynchronized('fat')]"
                        :max="100"
                        :min="1"
                        :step="1"
                        :thumb-size="24"
                    ></v-slider>
                  </v-flex>

                  <v-flex xs2 class="text-xs-center">
                    {{ calculateGramsFromPercentage(percentage.fat, 'Tłuszcz') | integers }}g
                  </v-flex>

                </v-layout>
              </v-form>
            </v-card-text>
          </v-card>
          <v-dialog
              v-model="timePickerMenu"
              lazy
              hide-overlay
              full-width
              max-width="290px"
              min-width="290px"
          >
            <v-time-picker format="24hr" v-model="time"></v-time-picker>
          </v-dialog>

          <v-divider></v-divider>
          <v-layout align-center justify-end row fill-height>
            <v-btn @click="step = 1">{{ 'app.previous' | trans }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
            <v-btn color="primary" :disabled="!step2Valid || loading" @click="save" v-if="!model.schema"
                   :loading="loading">
              {{ 'app.save' | trans }}
            </v-btn>
            <v-btn color="primary" :disabled="!step2Valid" @click="step = 3" v-else>
              {{ 'app.next' | trans }}
            </v-btn>
          </v-layout>
        </v-stepper-content>
        <v-stepper-content step="3" v-show="model.schema">
          <v-card v-if="model.desired_diet_energy" flat>
            <v-form lazy-validation v-model="step3Valid" ref="step3form" @input="validateStepThree">
              <v-container grid-list-md text-xs-center>
                <v-layout row wrap justify-center align-items-center>
                  <v-flex xs12 md4 offset-md6>
                    <v-radio-group v-model="inputMode" row>
                      <v-radio :label="`%`" value="percentage" :color="`success`"></v-radio>
                      <v-radio :class="`kcal-input-mode-radio`" :label="`app.energy` | trans" value="energy"
                               :color="`success`"></v-radio>
                    </v-radio-group>
                  </v-flex>
                </v-layout>
                <div v-for="(meal, index) in sortedSchema">
                  <v-layout row wrap justify-center align-items-center>
                    <v-flex xs12 md5>
                      <v-text-field
                          v-model="meal.name"
                          :counter="255"
                          :label="trans('app.meal_name')"
                          :rules="rules.mealName"
                          required
                      ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md2>
                      <v-text-field
                          :label="trans('app.hour')"
                          prepend-icon="access_time"
                          v-model="meal.time"
                          :rules="rules.hours"
                          @click="openTimePicker(meal)"
                          readonly
                      ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md2>
                      <v-text-field
                          :disabled="inputMode === 'energy'"
                          :value="meal.energy"
                          :label="trans('app.energy_percentage')"
                          suffix="%"
                          max="100"
                          required
                          step="1"
                          type="number"
                          :rules="[atLeastOneHundred()]"
                          @input="calculateEnergy($event, index)"
                      ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md2>
                      <v-text-field
                          :disabled="inputMode === 'percentage'"
                          :value="meal.kcal"
                          :label="trans('app.energy')"
                          required
                          suffix="kcal"
                          type="number"
                          :rules="[validateDesiredEnergy(index)]"
                          @input="calculatePercentage($event, index)"
                      ></v-text-field>
                    </v-flex>


                    <v-flex xs12 md1>
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-btn
                              v-on="on"
                              flat
                              icon
                              color="red"
                              class="mr-0 ml-0"
                              @click="model.schema.splice(index, 1)"
                              :disabled="model.schema.length === 1"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ 'app.delete' | trans }}</span>
                      </v-tooltip>
                    </v-flex>
                  </v-layout>
                </div>

                <v-divider></v-divider>

                <v-layout row>
                  <v-flex xs5>
                    <v-btn small flat color="success"
                           @click="model.schema.push({ name: '', time: '', energy: null, kcal: null, })">
                      <v-icon dark>add</v-icon>
                      {{ 'app.add_meal' | trans }}
                    </v-btn>
                  </v-flex>
                  <v-flex xs2 offset-xs2>
                    <p class="text-xs-right">
                      {{ totalEnergy | round }} / 100 %
                    </p>
                  </v-flex>
                  <v-flex xs2>
                    <p class="text-xs-right">
                      {{ totalKcal | round }} / {{ this.model.desired_diet_energy }} kcal
                    </p>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-card>
          <v-divider></v-divider>
          <v-layout align-center justify-end row fill-height>
            <v-btn @click="step = 2">{{ 'app.previous' | trans }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
            <v-btn color="primary" @click="save" :disabled="!step3Valid || loading" :loading="loading">
              {{ 'app.save' | trans }}
            </v-btn>
          </v-layout>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-dialog>
</template>

<script>
import debounce from 'lodash/debounce';
import {mapActions, mapGetters, mapState} from 'vuex';
import {trans} from '../app';
import {ErrorsBag} from '../utils/ErrorsBag';
import {round} from "../utils/money";
import {helpers} from "../helpers";
import RulesBuilder from "../utils/rulesBuilder";

export default {
  props: {
    externalModel: Object,
    modelId: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: 'create',
    },
  shouldBeOpened: {
        type: Boolean,
        default: false
      }},
  data: function () {
    return {
        rulesBuilder: new RulesBuilder(),
        time: null,
        timePickerMenu: false,
        timeEditedMeal: null,
        syncDiet: '',
        syncSchemas: '',
        dialog: false,
        loading: false,
        step: 1,
        model: null,
        titleError: [],
        percentage: {
          fat: 0,
          carbo: 0,
          protein: 0,
        },
        baseModel: {
          desired_diet_energy: null,
          days: 7,
          tags: [],
          nutrients: [
            {
              name: 'Białko',
              value: 0,
            },
            {
              name: 'Tłuszcz',
              value: 0,
            },
            {
              name: 'Węglowodany',
              value: 0,
            },
          ],
          system: false,
          schema: [
            {
              name: 'Śniadanie',
              time: '08:00',
              energy: 25,
              kcal: 0,
            },
            {
              name: 'II Śniadanie',
              time: '10:00',
              energy: 10,
              kcal: 0,
            },
            {
              name: 'Obiad',
              time: '14:00',
              energy: 30,
              kcal: 0,
            },
            {
              name: 'Podwieczorek',
              time: '17:00',
              energy: 15,
              kcal: 0,
            },
            {
              name: 'Kolacja',
              time: '20:00',
              energy: 20,
              kcal: 0,
            },
          ],
        },
        valid: false,
        step2Valid: true,
        step3Valid: true,
        gramToEnergy: {
          Białko: 4,
          Tłuszcz: 9,
          Węglowodany: 4,
        },
        errors: null,
        inputMode: 'energy',
      }
  },
  created() {
    this.errors = new ErrorsBag();
    this.model = helpers.deepClone(this.baseModel);
    this.titleError = [];
    this.debounceValidateName = debounce(this.validateName, 200);
    this.debounceRecalculatePercentage = debounce(this.recalculatePercentage, 200);
    this.dialog = this.shouldBeOpened;
  },
  computed: {
    ...mapState(['dictionaries']),
    ...mapGetters(['hasPermission', 'maxMenuDaysLength']),
    rules: function () {
      return {
        title: [
          v => !!v || trans('validation.required', {'attribute': trans('app.name')}),
          v => v && v.length <= 255 || trans('validation.max.string', {
            attribute: trans('app.name'),
            max: 255,
          }),
          v => v && v.length >= 4 || trans('validation.min.string', {
            attribute: trans('app.name'),
            min: 4,
          }),
        ],
        desired_diet_energy: [
          v => v && v >= 100 && v <= 10000 || trans('validation.between.numeric', {
            attribute: trans('app.desired_diet_energy'),
            min: 100,
            max: 10000,
          }),
        ],

        mealName: [
          v => !!v || trans('validation.required', {'attribute': trans('app.meal_name')}),
          v => v && v.length <= 255 || trans('validation.max.string', {
            attribute: trans('app.meal_name'),
            max: 255,
          }),
        ],

        hours: [
          v => !!v || trans('validation.required', {'attribute': trans('app.hour')}),
          v => v && v.length <= 255 || trans('validation.max.string', {
            attribute: trans('app.hour'),
            max: 255,
          }),
        ],

        days: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.number_of_days'))
              .addIsIntegerRule(trans('app.number_of_days'))
              .addNumberBetweenRule(trans('app.number_of_days'), 1, this.maxMenuDaysLength)
              .getRules()
        ],
      };
    },
    schemasItems() {
      return [...this.dictionaries.schemas, ...this.schemasTags];
    },
    dietsItems() {
      return [...this.dictionaries.diets, ...this.dietsTags];
    },
    totalKcal() {
      if (this.model && this.model.schema !== undefined) {
        return this.model.schema.map(s => s.kcal).reduce((p, c) => p + c, 0);
      }

      return 0;
    },
    totalEnergy() {
      if (this.model && this.model.schema !== undefined) {
        return this.model.schema.map(s => s.energy).reduce((p, c) => p + c, 0);
      }

      return 0;
    },
    formTitle() {
      return trans(this.mode === 'create' ? 'app.create' : 'app.edit') + ' ' + trans('page.schemas.schema').toLowerCase();
    },

    sortedSchema() {
      if (this.model.schema !== undefined) {
        this.sortSchema();
        return this.model.schema;
      }
    },
    schemasTags: {
      get() {
        return this.getTags('schema');
      },
      set(tags) {
        this.setTags(tags, 'schema');
      },
    },

    dietsTags: {
      get() {
        return this.getTags('diet');
      },
      set(tags) {
        this.setTags(tags, 'diet');
      },
    },
  },
  methods: {
    ...mapActions(['FETCH_DICTIONARIES']),
    recalculatePercentage() {

      if (isNaN(this.percentage.protein) || this.percentage.protein === 0) {
        this.percentage.protein = Math.round(this.calculatePercentageFromGrams(this.getNutrient('Białko').value, 'Białko'));
      } else {
        this.getNutrient('Białko').value = Math.round(this.calculateGramsFromPercentage(this.percentage.protein, 'Białko'));
      }

      if (isNaN(this.percentage.carbo) || this.percentage.carbo === 0) {
        this.percentage.carbo = Math.round(this.calculatePercentageFromGrams(this.getNutrient('Węglowodany').value, 'Węglowodany'));
      } else {
        this.getNutrient('Węglowodany').value = Math.round(this.calculateGramsFromPercentage(this.percentage.carbo, 'Węglowodany'));
      }

      if (isNaN(this.percentage.fat) || this.percentage.fat === 0) {
        this.percentage.fat = Math.round(this.calculatePercentageFromGrams(this.getNutrient('Tłuszcz').value, 'Tłuszcz'));
      } else {
        this.getNutrient('Tłuszcz').value = Math.round(this.calculateGramsFromPercentage(this.percentage.fat, 'Tłuszcz'));
      }
    },

    validateStepTwo() {
      if (this.step === 1) {
        return true;
      }

      return this.step2Valid;
    },
    validateStepThree() {
      if (this.$refs.step3form !== undefined) {
        this.$refs.step3form.validate();
      }
    },
    async validateName() {
      if (!this.model.title) {
        return true;
      }

      const {data} = await axios.get(`/schemas/data?title=${this.model.title}`);

      if (data.meta.total === 0 || data.data[0].id === this.model.id) {
        this.titleError = [];
        return;
      }

      this.titleError.push(trans('validation.unique', {'attribute': trans('app.title')}));
    },
    atLeastOneHundred() {
      return (v) => round(this.totalEnergy, 2) === 100 || trans('app.nutrients.validation.sum_has_to_equal', {attribute: '100%'});
    },

    validateDesiredEnergy(index) {
      const sum = this.model.schema.filter((v, i) => i !== index).map((v) => v.kcal).reduce((p, v) => p + v, 0);

      return (v) => sum + v === this.model.desired_diet_energy || trans('app.nutrients.validation.sum_has_to_equal', {attribute: `${this.model.desired_diet_energy} kcal`});
    },

    schynchronized(name) {
      const sum = ['fat', 'carbo', 'protein'].filter(n => n !== name).map(n => this.percentage[n]).reduce((p, v) => p + v, 0);

      return (v) => sum + v === 100 || trans('app.nutrients.validation.sum_has_to_be_100');
    },
    getTags(type) {
      return this.model.tags && this.model.tags.length > 0 ? this.model.tags.filter(t => t.type === type) : [];
    },
    setTags(tags, type) {
      tags.forEach((value, index) => {
        if (typeof value === 'string')
          tags[index] = {
            name: value,
            type,
            id: null,
          };
      });

      this.model.tags = this.model.tags.filter(t => t.type !== type);
      this.model.tags.push(...tags);
    },

    close() {
      this.dialog = false;
    },

    resetDialog() {
      this.mode = 'create';
      this.model = null;
      this.errors.clear();
      this.percentage.protein = 0;
      this.percentage.fat = 0;
      this.percentage.carbo = 0;
      this.titleError = [];
      this.step = 1;
      this.inputMode = 'energy';
      this.$emit('close');
    },

    async save() {
      try {
        this.loading = true;
        if (this.model.id) {
          await axios.put('/schemas/' + this.model.id, this.model);
        } else {
          const response = await axios.post('/schemas', this.model);
          window.location = `/schemas/${response.data.id}`;
          return;
        }
        this.loading = false;
        this.close();
        this.$toast.success(trans('app.actions.saved', {name: trans('app.schema').toLowerCase()}));

        this.FETCH_DICTIONARIES();

        this.$emit('stored');

      } catch (error) {
        this.loading = false;
        if (error.response && error.response.status === 422) {
          this.errors.record(error.response.data.errors);
        } else {
          this.sentryCaptureException(error);
        }
      }
    },

    handleStepThree() {
      if (this.model.schema !== undefined) {
        this.recalculateEnergyAssigment();
      }
    },
    validate(step) {
      if (this.$refs.form.validate()) {
        this.step = step;
      }
    },

    calculateGramsFromPercentage(value, name) {
      return (value * this.model.desired_diet_energy / 100) / this.gramToEnergy[name];
    },

    calculatePercentageFromGrams(value, name) {
      return (value / this.model.desired_diet_energy * 100) * this.gramToEnergy[name];
    },

    formUpdated(e) {
      if (this.$refs.step2form)
        this.$refs.step2form.validate();
    },

    getNutrient(name) {
      if (!this.model)
        return;

      const nutrient = this.model.nutrients.find(n => n.name === name);

      if (nutrient) {
        return nutrient;
      }

      return {value: 0};
    },

    calculateEnergy(percentage, index) {
      if (percentage < 0.01) {
          percentage = 0.01;
        }
        const value = round(percentage * this.model.desired_diet_energy / 100, 0);

      this.model.schema[index].kcal = value;
      this.model.schema[index].energy = Number(percentage);
      if (this.totalEnergy === 100) {
        const difference = round(this.model.desired_diet_energy - this.totalKcal, 0);
        const lastIndex = this.model.schema.length - 1;
        this.model.schema[lastIndex].kcal = round(this.model.schema[lastIndex].kcal + difference, 0);
      }

      return value;
    },

    calculatePercentage(kcal, index) {
      if (kcal < 1) {
          kcal = 1;
        }
        const value = round(kcal / this.model.desired_diet_energy * 100, 2);

      this.model.schema[index].energy = value;
      this.model.schema[index].kcal = Number(kcal);
      if (this.totalKcal === this.model.desired_diet_energy) {
        const difference = round(100 - round(this.totalEnergy, 2), 2);
        const lastIndex = this.model.schema.length - 1;
        this.model.schema[lastIndex].energy = round(this.model.schema[lastIndex].energy + difference, 2);
      }

      return value;
    },

    recalculateEnergyAssigment() {
      this.model.schema.forEach((s, i) => {
        this.calculateEnergy(s.energy, i);
      });

      const rest = this.model.desired_diet_energy - this.totalKcal;

      if (rest > 0) {
        const indexOfMaxValue = this.model.schema.reduce((max, x, i, arr) => x > arr[max] ? i : max, 0);
        this.model.schema[indexOfMaxValue].kcal += rest;
      }
    },

    sortSchema() {
      this.model.schema.sort((a, b) => {
        return moment(a.time, 'HH:mm').unix() - moment(b.time, 'HH:mm').unix();
      });
    },
    openTimePicker(meal) {
      this.timePickerMenu = true;
      this.time = meal.time;
      this.timeEditedMeal = meal;
    },
  },
  watch: {
    externalModel: function (value) {
      if (value) {
        this.model = {...value};
        this.dialog = true;
      }
    },
    timePickerMenu(timePickerMenu) {
      if (!timePickerMenu && this.timeEditedMeal) {
        this.timeEditedMeal.time = this.time;
        this.time = null;
        this.timeEditedMeal = null;
        this.sortSchema();
      }
    },
    step: function (step) {
      if (step === 3) {
        this.handleStepThree();
      }
    },
    'model.desired_diet_energy': function (value) {
      if (value) {
        this.debounceRecalculatePercentage();
      }
    },
    'percentage.fat': function (value) {
      if (isNaN(value) || !this.model) {
        return;
      }
      this.getNutrient('Tłuszcz').value = Math.round(this.calculateGramsFromPercentage(value, 'Tłuszcz'));
    },
    'percentage.protein': function (value) {
      if (isNaN(value) || !this.model) {
        return;
      }

      this.getNutrient('Białko').value = Math.round(this.calculateGramsFromPercentage(value, 'Białko'));
    },
    'percentage.carbo': function (value) {
      if (isNaN(value) || !this.model) {
        return;
      }

      this.getNutrient('Węglowodany').value = Math.round(this.calculateGramsFromPercentage(value, 'Węglowodany'));
    },
    dialog: function (dialog) {

      if (dialog === false) {
        this.resetDialog();
      }

      if (dialog && this.modelId) {
        axios.get(`/schemas/${this.modelId}/fetch`).then((r) => {
          this.model = {...r.data.data};
        }).catch((error) => {
          this.sentryCaptureException(error);
        });

        return;
      }

      if (dialog && !this.externalModel) {
        this.model = helpers.deepClone(this.baseModel);
        this.percentage.protein = 13;
        this.percentage.fat = 28;
        this.percentage.carbo = 59;
        this.step = 1;
      }
    },
  },
};
</script>

<style scoped>
.kcal-input-mode-radio {
  margin-left: 80px;
}
</style>
